// Live's transport, over an ArrangementPlayer.
//
// Live keeps two positions. The INSERT MARKER is where you last clicked in
// a track or what you last selected. Space plays from it, and a stop puts
// the playhead back on it, so "listen again from here" is one key. Where
// playback last STOPPED is kept too, for Shift+Space's "continue". A click
// in the ruler plays from there; if the transport is already playing, it
// jumps there on the next launch-quantization line so the music stays in
// time. React-free, so it can be tested against the stub AudioContext; the
// hook (useArrangementPlayer) wraps it and mirrors the marker into state.

import type { ArrangementPlayer } from "@web-shared/lib/audio-engine";

export class LiveTransport {
  /** Where Space plays from, in seconds. */
  insertSec = 0;
  /** Where playback last stopped, for Shift+Space. */
  lastStopSec: number | null = null;
  /** This run was started by the transport: when it ends by itself (the
   * selection's end, the Set's end), go back to the marker. */
  private returnOnStop = false;

  constructor(
    private readonly player: ArrangementPlayer,
    /** Start playing from a position, loading whatever isn't loaded yet
     * (the hook's job: it owns loading state). */
    private readonly start: (fromSec: number) => void,
    /** The marker moved (the hook mirrors it into React state). */
    private readonly onInsert: (sec: number) => void = () => undefined,
  ) {}

  /** Move the insert marker. Stopped, the playhead moves with it; playing,
   * the music plays on. */
  setInsert(sec: number): void {
    this.insertSec = Math.max(0, sec);
    this.onInsert(this.insertSec);
    if (!this.player.isPlaying) this.player.seek(this.insertSec);
  }

  /** Stop the arrangement; the playhead goes back to the insert marker. */
  stop(): void {
    if (this.player.isPlaying) {
      this.lastStopSec = this.player.position;
      this.player.pause();
    }
    this.player.stopAt = null;
    this.returnOnStop = false;
    this.player.seek(this.insertSec);
  }

  /** Space: play from the insert marker, or stop. */
  playFromInsert(): void {
    if (this.player.isPlaying) return this.stop();
    this.run(this.insertSec, null);
  }

  /** Shift+Space: continue from where playback last stopped, or stop. */
  resume(): void {
    if (this.player.isPlaying) return this.stop();
    this.run(this.lastStopSec ?? this.insertSec, null);
  }

  /** Alt+Space: play [from, to) once, then back to the marker; or stop. */
  playRange(fromSec: number, toSec: number): void {
    if (this.player.isPlaying) return this.stop();
    this.run(fromSec, toSec > fromSec ? toSec : null);
  }

  /** Double-click Stop / Home: the marker back to the start. */
  returnToStart(): void {
    this.setInsert(0);
  }

  /** A click in the ruler. Playing: jump there on the next quantization
   * line. Stopped: play from there. It moves the insert marker too. */
  jumpTo(sec: number): void {
    const at = Math.max(0, sec);
    this.insertSec = at;
    this.onInsert(at);
    if (this.player.isPlaying) {
      void this.player.jumpQuantized(at);
      return;
    }
    this.run(at, null);
  }

  /** Call once per frame after `player.tick()`: a run that ended by
   * itself goes back to the marker, as a stop would. Returns whether it did. */
  afterTick(): boolean {
    if (this.player.isPlaying || !this.returnOnStop) return false;
    this.returnOnStop = false;
    this.lastStopSec = this.player.position;
    this.player.seek(this.insertSec);
    return true;
  }

  /** Someone else is driving the player (plain pause/resume): forget. */
  release(): void {
    this.returnOnStop = false;
    this.player.stopAt = null;
  }

  private run(fromSec: number, stopAt: number | null): void {
    this.returnOnStop = true;
    this.player.stopAt = stopAt;
    this.start(fromSec);
  }
}
